/**
 * Workflow Service - Handles API interactions for business workflows and processes
 */

import axios from 'axios';
import { post } from '../utils/apiUtils';

const API_URL = '/api/workflows';
const EXECUTOR_URL = '/api/v1/mms_ai_executors/workflow';

/**
 * Get all workflows with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of workflows
 */
export const getWorkflows = async (filters = {}) => {
  try {
    const response = await axios.get(API_URL, { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching workflows:', error);
    throw error;
  }
};

/**
 * Get a specific workflow by ID
 * @param {string} workflowId - Workflow ID
 * @returns {Promise<Object>} Promise resolving to workflow object
 */
export const getWorkflowById = async (workflowId) => {
  try {
    const response = await axios.get(`${API_URL}/${workflowId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Create a new workflow
 * @param {Object} workflowData - Workflow data
 * @returns {Promise<Object>} Promise resolving to created workflow
 */
export const createWorkflow = async (workflowData) => {
  try {
    const response = await axios.post(API_URL, workflowData);
    return response.data;
  } catch (error) {
    console.error('Error creating workflow:', error);
    throw error;
  }
};

/**
 * Update an existing workflow
 * @param {string} workflowId - Workflow ID
 * @param {Object} workflowData - Updated workflow data
 * @returns {Promise<Object>} Promise resolving to updated workflow
 */
export const updateWorkflow = async (workflowId, workflowData) => {
  try {
    const response = await axios.put(`${API_URL}/${workflowId}`, workflowData);
    return response.data;
  } catch (error) {
    console.error(`Error updating workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Delete a workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise<void>} Promise resolving when workflow is deleted
 */
export const deleteWorkflow = async (workflowId) => {
  try {
    await axios.delete(`${API_URL}/${workflowId}`);
  } catch (error) {
    console.error(`Error deleting workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Get workflow stages
 * @param {string} workflowId - Workflow ID
 * @returns {Promise<Array>} Promise resolving to array of workflow stages
 */
export const getWorkflowStages = async (workflowId) => {
  try {
    const response = await axios.get(`${API_URL}/${workflowId}/stages`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching stages for workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Add a stage to a workflow
 * @param {string} workflowId - Workflow ID
 * @param {Object} stageData - Stage data
 * @returns {Promise<Object>} Promise resolving to created stage
 */
export const addWorkflowStage = async (workflowId, stageData) => {
  try {
    const response = await axios.post(`${API_URL}/${workflowId}/stages`, stageData);
    return response.data;
  } catch (error) {
    console.error(`Error adding stage to workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Update a workflow stage
 * @param {string} workflowId - Workflow ID
 * @param {string} stageId - Stage ID
 * @param {Object} stageData - Updated stage data
 * @returns {Promise<Object>} Promise resolving to updated stage
 */
export const updateWorkflowStage = async (workflowId, stageId, stageData) => {
  try {
    const response = await axios.put(`${API_URL}/${workflowId}/stages/${stageId}`, stageData);
    return response.data;
  } catch (error) {
    console.error(`Error updating stage ${stageId} in workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Delete a workflow stage
 * @param {string} workflowId - Workflow ID
 * @param {string} stageId - Stage ID
 * @returns {Promise<void>} Promise resolving when stage is deleted
 */
export const deleteWorkflowStage = async (workflowId, stageId) => {
  try {
    await axios.delete(`${API_URL}/${workflowId}/stages/${stageId}`);
  } catch (error) {
    console.error(`Error deleting stage ${stageId} from workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Get workflow transitions
 * @param {string} workflowId - Workflow ID
 * @returns {Promise<Array>} Promise resolving to array of workflow transitions
 */
export const getWorkflowTransitions = async (workflowId) => {
  try {
    const response = await axios.get(`${API_URL}/${workflowId}/transitions`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching transitions for workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Add a transition to a workflow
 * @param {string} workflowId - Workflow ID
 * @param {Object} transitionData - Transition data
 * @returns {Promise<Object>} Promise resolving to created transition
 */
export const addWorkflowTransition = async (workflowId, transitionData) => {
  try {
    const response = await axios.post(`${API_URL}/${workflowId}/transitions`, transitionData);
    return response.data;
  } catch (error) {
    console.error(`Error adding transition to workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Update a workflow transition
 * @param {string} workflowId - Workflow ID
 * @param {string} transitionId - Transition ID
 * @param {Object} transitionData - Updated transition data
 * @returns {Promise<Object>} Promise resolving to updated transition
 */
export const updateWorkflowTransition = async (workflowId, transitionId, transitionData) => {
  try {
    const response = await axios.put(`${API_URL}/${workflowId}/transitions/${transitionId}`, transitionData);
    return response.data;
  } catch (error) {
    console.error(`Error updating transition ${transitionId} in workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Delete a workflow transition
 * @param {string} workflowId - Workflow ID
 * @param {string} transitionId - Transition ID
 * @returns {Promise<void>} Promise resolving when transition is deleted
 */
export const deleteWorkflowTransition = async (workflowId, transitionId) => {
  try {
    await axios.delete(`${API_URL}/${workflowId}/transitions/${transitionId}`);
  } catch (error) {
    console.error(`Error deleting transition ${transitionId} from workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Get workflow instances
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of workflow instances
 */
export const getWorkflowInstances = async (filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/instances`, { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching workflow instances:', error);
    throw error;
  }
};

/**
 * Get a specific workflow instance by ID
 * @param {string} instanceId - Instance ID
 * @returns {Promise<Object>} Promise resolving to workflow instance object
 */
export const getWorkflowInstanceById = async (instanceId) => {
  try {
    const response = await axios.get(`${API_URL}/instances/${instanceId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching workflow instance ${instanceId}:`, error);
    throw error;
  }
};

/**
 * Create a new workflow instance
 * @param {string} workflowId - Workflow ID
 * @param {Object} instanceData - Instance data
 * @returns {Promise<Object>} Promise resolving to created instance
 */
export const createWorkflowInstance = async (workflowId, instanceData) => {
  try {
    const response = await axios.post(`${API_URL}/${workflowId}/instances`, instanceData);
    return response.data;
  } catch (error) {
    console.error(`Error creating instance for workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Update a workflow instance
 * @param {string} instanceId - Instance ID
 * @param {Object} instanceData - Updated instance data
 * @returns {Promise<Object>} Promise resolving to updated instance
 */
export const updateWorkflowInstance = async (instanceId, instanceData) => {
  try {
    const response = await axios.put(`${API_URL}/instances/${instanceId}`, instanceData);
    return response.data;
  } catch (error) {
    console.error(`Error updating workflow instance ${instanceId}:`, error);
    throw error;
  }
};

/**
 * Get workflow instance history
 * @param {string} instanceId - Instance ID
 * @returns {Promise<Array>} Promise resolving to array of history entries
 */
export const getWorkflowInstanceHistory = async (instanceId) => {
  try {
    const response = await axios.get(`${API_URL}/instances/${instanceId}/history`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching history for workflow instance ${instanceId}:`, error);
    throw error;
  }
};

/**
 * Transition a workflow instance to a new stage
 * @param {string} instanceId - Instance ID
 * @param {string} transitionId - Transition ID
 * @param {Object} data - Transition data
 * @returns {Promise<Object>} Promise resolving to updated instance
 */
export const transitionWorkflowInstance = async (instanceId, transitionId, data = {}) => {
  try {
    const response = await axios.post(`${API_URL}/instances/${instanceId}/transition`, { 
      transitionId,
      ...data
    });
    return response.data;
  } catch (error) {
    console.error(`Error transitioning workflow instance ${instanceId}:`, error);
    throw error;
  }
};

/**
 * Get workflow metrics
 * @param {string} workflowId - Workflow ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to workflow metrics
 */
export const getWorkflowMetrics = async (workflowId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${workflowId}/metrics`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching metrics for workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Execute workflow synchronously with recommendation payload
 * @param {Object} params - Workflow execution parameters
 * @param {string} params.workflow_cd - Workflow code from workbenchData.manualTask.pipeline_cd
 * @param {number} params.resume_workflow_from_step_instance_id - Node execution ID from workbenchData.manualTask.node_execution_id
 * @param {string} params.resume_workflow_from - Task code from workbenchData.manualTask.code
 * @param {string} params.pipeline_execution_id - Pipeline ID from workbenchData.manualTask.pipeline_id
 * @param {Array} params.in_detail_entities - Detail entities from workbenchData.manualTask.inDetailEntities
 * @param {Object} params.selected_action - Selected recommendation object
 * @returns {Promise<Object>} Promise resolving to workflow execution result
 */
export const executeWorkflowSync = async ({
  workflow_cd,
  resume_workflow_from_step_instance_id,
  resume_workflow_from,
  pipeline_execution_id,
  in_detail_entities,
  session_id,
  user_id,
  selected_action
}) => {
  try {
    const payload = {
      workflow_cd,
      resume_workflow_from_step_instance_id,
      resume_workflow_from,
      "query":"",
      pipeline_execution_id,
      user_id,
      session_id,
      payload: {
        in_detail_entities,
        selected_action
      }
    };
    
    const response = await post(`${EXECUTOR_URL}/execute/sync`, payload);
    return response;
  } catch (error) {
    console.error('Error executing workflow:', error);
    throw error;
  }
};

export default {
  getWorkflows,
  getWorkflowById,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  getWorkflowStages,
  addWorkflowStage,
  updateWorkflowStage,
  deleteWorkflowStage,
  getWorkflowTransitions,
  addWorkflowTransition,
  updateWorkflowTransition,
  deleteWorkflowTransition,
  getWorkflowInstances,
  getWorkflowInstanceById,
  createWorkflowInstance,
  updateWorkflowInstance,
  getWorkflowInstanceHistory,
  transitionWorkflowInstance,
  getWorkflowMetrics,
  executeWorkflowSync
};
